/**
 * Wishlist/geo CTA static catalog shared by WishlistAdmin, persistence builder, and CI validation.
 *
 * Keep in sync with:
 * - cloudflare-worker-anaafernandez/worker.js → normalizeWishlistCityKey / detectByCity
 * - sql/052_wishlist_city_ar_caba_gba.sql
 */

import { WORLD_COUNTRIES } from "@/lib/worldCountries";
import { AR_NATIONAL_CITY_CATALOG } from "./arNationalCityCatalog";

export type GeoCtaType = "none" | "birthday" | "wishlist" | "community" | "dual_video";

/** Provincias AR en el mismo orden y nombres que usa el Cloudflare Worker */
export const AR_PROVINCES: { code: string; name: string; region: string }[] = [
  { code: "CABA",                 name: "CABA",                 region: "CABA" },
  { code: "Buenos Aires",         name: "Buenos Aires",         region: "GBA" },
  { code: "Córdoba",              name: "Córdoba",              region: "Centro" },
  { code: "Santa Fe",             name: "Santa Fe",             region: "Centro" },
  { code: "Mendoza",              name: "Mendoza",              region: "Cuyo" },
  { code: "Tucumán",              name: "Tucumán",              region: "NOA" },
  { code: "Entre Ríos",           name: "Entre Ríos",           region: "Litoral" },
  { code: "Salta",                name: "Salta",                region: "NOA" },
  { code: "Misiones",             name: "Misiones",             region: "NEA" },
  { code: "Chaco",                name: "Chaco",                region: "NEA" },
  { code: "Corrientes",           name: "Corrientes",           region: "NEA" },
  { code: "Santiago del Estero",  name: "Santiago del Estero",  region: "NOA" },
  { code: "San Juan",             name: "San Juan",             region: "Cuyo" },
  { code: "Jujuy",                name: "Jujuy",                region: "NOA" },
  { code: "Río Negro",            name: "Río Negro",            region: "Patagonia" },
  { code: "Neuquén",              name: "Neuquén",              region: "Patagonia" },
  { code: "Formosa",              name: "Formosa",              region: "NEA" },
  { code: "Chubut",               name: "Chubut",               region: "Patagonia" },
  { code: "San Luis",             name: "San Luis",             region: "Cuyo" },
  { code: "Catamarca",            name: "Catamarca",            region: "NOA" },
  { code: "La Rioja",             name: "La Rioja",             region: "NOA" },
  { code: "Santa Cruz",           name: "Santa Cruz",           region: "Patagonia" },
  { code: "La Pampa",             name: "La Pampa",             region: "Patagonia" },
  { code: "Tierra del Fuego",     name: "Tierra del Fuego",     region: "Patagonia" },
];

export type CityAlias = { geo_code: string; name: string };

/** Catálogo CABA+BA: `provinceCode` alinea con AR_PROVINCES[].code. */
export type CityCatalogEntry = {
  geo_code: string;
  name: string;
  region: string;
  provinceCode: string;
  aliases?: CityAlias[];
};

/** Fila antes de attach de provinceCode jurídica. */
export type CityCatalogRow = Omit<CityCatalogEntry, "provinceCode">;

export function deriveCatalogCityProvinceCode(region: string): string {
  if (region === "CABA principal" || region.startsWith("Barrios CABA")) {
    return "CABA";
  }
  if (
    region.startsWith("GBA") ||
    region === "Interior BA" ||
    region === "Costa BA"
  ) {
    return "Buenos Aires";
  }
  const byProvinceName = AR_PROVINCES.find((p) => p.name === region);
  if (byProvinceName) return byProvinceName.code;
  return "Buenos Aires";
}

const AR_CABA_GBA_CITY_ROWS: CityCatalogRow[] = [
    {
      geo_code: "caba",
      name: "CABA (Ciudad Autónoma)",
      region: "CABA principal",
      aliases: [
        { geo_code: "capital federal",                 name: "Capital Federal" },
        { geo_code: "autonomous city of buenos aires", name: "Autonomous City of Buenos Aires" },
        { geo_code: "ciudad autonoma de buenos aires", name: "Ciudad Autónoma de Buenos Aires" },
        { geo_code: "buenos aires city",               name: "Buenos Aires City" },
        /** Cloudflare / Telecom AR reportan cf.city="Buenos Aires" para porteños (sin barrio). */
        { geo_code: "buenos aires",                    name: "Buenos Aires (CF → CABA)" },
      ],
    },
    { geo_code: "palermo",            name: "Palermo",            region: "Barrios CABA" },
    { geo_code: "palermo soho",       name: "Palermo Soho",       region: "Barrios CABA" },
    { geo_code: "palermo hollywood",  name: "Palermo Hollywood",  region: "Barrios CABA" },
    { geo_code: "palermo chico",      name: "Palermo Chico",      region: "Barrios CABA" },
    { geo_code: "las canitas",        name: "Las Cañitas",        region: "Barrios CABA" },
    { geo_code: "recoleta",           name: "Recoleta",           region: "Barrios CABA" },
    { geo_code: "belgrano",           name: "Belgrano",           region: "Barrios CABA" },
    { geo_code: "nunez",              name: "Núñez",              region: "Barrios CABA" },
    { geo_code: "colegiales",         name: "Colegiales",         region: "Barrios CABA" },
    { geo_code: "saavedra",           name: "Saavedra",           region: "Barrios CABA" },
    { geo_code: "villa urquiza",      name: "Villa Urquiza",      region: "Barrios CABA" },
    { geo_code: "villa pueyrredon",   name: "Villa Pueyrredón",   region: "Barrios CABA" },
    { geo_code: "villa ortuzar",      name: "Villa Ortúzar",      region: "Barrios CABA" },
    { geo_code: "chacarita",          name: "Chacarita",          region: "Barrios CABA" },
    { geo_code: "villa crespo",       name: "Villa Crespo",       region: "Barrios CABA" },
    { geo_code: "almagro",            name: "Almagro",            region: "Barrios CABA" },
    { geo_code: "boedo",              name: "Boedo",              region: "Barrios CABA" },
    { geo_code: "caballito",          name: "Caballito",          region: "Barrios CABA" },
    { geo_code: "flores",             name: "Flores",             region: "Barrios CABA" },
    { geo_code: "floresta",           name: "Floresta",           region: "Barrios CABA" },
    { geo_code: "monte castro",       name: "Monte Castro",       region: "Barrios CABA" },
    { geo_code: "villa devoto",       name: "Villa Devoto",       region: "Barrios CABA" },
    { geo_code: "villa del parque",   name: "Villa del Parque",   region: "Barrios CABA" },
    { geo_code: "villa luro",         name: "Villa Luro",         region: "Barrios CABA" },
    { geo_code: "villa real",         name: "Villa Real",         region: "Barrios CABA" },
    { geo_code: "versalles",          name: "Versalles",          region: "Barrios CABA" },
    { geo_code: "liniers",            name: "Liniers",            region: "Barrios CABA" },
    { geo_code: "mataderos",          name: "Mataderos",          region: "Barrios CABA" },
    { geo_code: "parque avellaneda",  name: "Parque Avellaneda",  region: "Barrios CABA" },
    { geo_code: "parque chacabuco",   name: "Parque Chacabuco",   region: "Barrios CABA" },
    { geo_code: "parque patricios",   name: "Parque Patricios",   region: "Barrios CABA" },
    { geo_code: "barracas",           name: "Barracas",           region: "Barrios CABA" },
    { geo_code: "la boca",            name: "La Boca",            region: "Barrios CABA" },
    { geo_code: "san telmo",          name: "San Telmo",          region: "Barrios CABA" },
    { geo_code: "constitucion",       name: "Constitución",       region: "Barrios CABA" },
    { geo_code: "san cristobal",      name: "San Cristóbal",      region: "Barrios CABA" },
    { geo_code: "balvanera",          name: "Balvanera",          region: "Barrios CABA" },
    { geo_code: "once",               name: "Once",               region: "Barrios CABA" },
    { geo_code: "congreso",           name: "Congreso",           region: "Barrios CABA" },
    {
      geo_code: "monserrat",
      name: "Monserrat",
      region: "Barrios CABA",
      aliases: [{ geo_code: "montserrat", name: "Montserrat (variante)" }],
    },
    { geo_code: "retiro",             name: "Retiro",             region: "Barrios CABA" },
    { geo_code: "microcentro",        name: "Microcentro",        region: "Barrios CABA" },
    { geo_code: "puerto madero",      name: "Puerto Madero",      region: "Barrios CABA" },
    { geo_code: "agronomia",          name: "Agronomía",          region: "Barrios CABA" },
    { geo_code: "paternal",           name: "Paternal",           region: "Barrios CABA" },
    { geo_code: "villa general mitre",name: "Villa General Mitre",region: "Barrios CABA" },
    { geo_code: "villa santa rita",   name: "Villa Santa Rita",   region: "Barrios CABA" },
    { geo_code: "villa lugano",       name: "Villa Lugano",       region: "Barrios CABA" },
    { geo_code: "villa soldati",      name: "Villa Soldati",      region: "Barrios CABA" },
    { geo_code: "villa riachuelo",    name: "Villa Riachuelo",    region: "Barrios CABA" },
    { geo_code: "nueva pompeya",      name: "Nueva Pompeya",      region: "Barrios CABA" },
    { geo_code: "villa madero",       name: "Villa Madero",       region: "Barrios CABA" },
    { geo_code: "vicente lopez",      name: "Vicente López",      region: "GBA Norte" },
    { geo_code: "san isidro",         name: "San Isidro",         region: "GBA Norte" },
    { geo_code: "san fernando",       name: "San Fernando",       region: "GBA Norte" },
    { geo_code: "tigre",              name: "Tigre",              region: "GBA Norte" },
    { geo_code: "escobar",            name: "Escobar",            region: "GBA Norte" },
    { geo_code: "belen de escobar",   name: "Belén de Escobar",   region: "GBA Norte" },
    { geo_code: "pilar",              name: "Pilar",              region: "GBA Norte" },
    { geo_code: "san miguel",         name: "San Miguel",         region: "GBA Norte" },
    { geo_code: "malvinas argentinas",name: "Malvinas Argentinas",region: "GBA Norte" },
    { geo_code: "jose c. paz",        name: "José C. Paz",        region: "GBA Norte" },
    { geo_code: "grand bourg",        name: "Grand Bourg",        region: "GBA Norte" },
    { geo_code: "san martin",         name: "San Martín",         region: "GBA Oeste" },
    { geo_code: "villa ballester",    name: "Villa Ballester",    region: "GBA Oeste" },
    { geo_code: "villa lynch",        name: "Villa Lynch",        region: "GBA Oeste" },
    { geo_code: "tres de febrero",    name: "Tres de Febrero",    region: "GBA Oeste" },
    { geo_code: "ciudadela",          name: "Ciudadela",          region: "GBA Oeste" },
    { geo_code: "moron",              name: "Morón",              region: "GBA Oeste" },
    { geo_code: "hurlingham",         name: "Hurlingham",         region: "GBA Oeste" },
    { geo_code: "ituzaingo",          name: "Ituzaingó",          region: "GBA Oeste" },
    { geo_code: "merlo",              name: "Merlo",              region: "GBA Oeste" },
    { geo_code: "moreno",             name: "Moreno",             region: "GBA Oeste" },
    { geo_code: "la matanza",         name: "La Matanza",         region: "GBA Oeste" },
    { geo_code: "san justo",          name: "San Justo",          region: "GBA Oeste" },
    { geo_code: "ramos mejia",        name: "Ramos Mejía",        region: "GBA Oeste" },
    { geo_code: "isidro casanova",    name: "Isidro Casanova",    region: "GBA Oeste" },
    { geo_code: "rafael castillo",    name: "Rafael Castillo",    region: "GBA Oeste" },
    { geo_code: "lomas del mirador",  name: "Lomas del Mirador",  region: "GBA Oeste" },
    { geo_code: "ciudad madero",      name: "Ciudad Madero",      region: "GBA Oeste" },
    { geo_code: "tapiales",           name: "Tapiales",           region: "GBA Oeste" },
    { geo_code: "avellaneda",         name: "Avellaneda",         region: "GBA Sur" },
    { geo_code: "lanus",              name: "Lanús",              region: "GBA Sur" },
    { geo_code: "lomas de zamora",    name: "Lomas de Zamora",    region: "GBA Sur" },
    { geo_code: "almirante brown",    name: "Almirante Brown",    region: "GBA Sur" },
    { geo_code: "adrogue",            name: "Adrogué",            region: "GBA Sur" },
    { geo_code: "temperley",          name: "Temperley",          region: "GBA Sur" },
    { geo_code: "burzaco",            name: "Burzaco",            region: "GBA Sur" },
    { geo_code: "claypole",           name: "Claypole",           region: "GBA Sur" },
    { geo_code: "longchamps",         name: "Longchamps",         region: "GBA Sur" },
    { geo_code: "monte grande",       name: "Monte Grande",       region: "GBA Sur" },
    { geo_code: "esteban echeverria", name: "Esteban Echeverría", region: "GBA Sur" },
    { geo_code: "ezeiza",             name: "Ezeiza",             region: "GBA Sur" },
    { geo_code: "canning",            name: "Canning",            region: "GBA Sur" },
    { geo_code: "tristan suarez",     name: "Tristán Suárez",     region: "GBA Sur" },
    { geo_code: "presidente peron",   name: "Presidente Perón",   region: "GBA Sur" },
    { geo_code: "san vicente",        name: "San Vicente",        region: "GBA Sur" },
    { geo_code: "quilmes",            name: "Quilmes",            region: "GBA Sur" },
    { geo_code: "berazategui",        name: "Berazategui",        region: "GBA Sur" },
    { geo_code: "florencio varela",   name: "Florencio Varela",   region: "GBA Sur" },
    { geo_code: "berisso",            name: "Berisso",            region: "GBA Sur" },
    { geo_code: "ensenada",           name: "Ensenada",           region: "GBA Sur" },
    { geo_code: "brandsen",           name: "Brandsen",           region: "GBA Sur" },
    { geo_code: "pablo nogues",       name: "Pablo Nogués",       region: "GBA Sur" },
    { geo_code: "la plata",           name: "La Plata",           region: "Interior BA" },
    { geo_code: "mar del plata",      name: "Mar del Plata",      region: "Costa BA" },
    { geo_code: "bahia blanca",       name: "Bahía Blanca",       region: "Interior BA" },
    { geo_code: "tandil",             name: "Tandil",             region: "Interior BA" },
    { geo_code: "olavarria",          name: "Olavarría",          region: "Interior BA" },
    { geo_code: "azul",               name: "Azul",               region: "Interior BA" },
    { geo_code: "necochea",           name: "Necochea",           region: "Costa BA" },
    { geo_code: "junin",              name: "Junín",              region: "Interior BA" },
    { geo_code: "pergamino",          name: "Pergamino",          region: "Interior BA" },
    { geo_code: "chivilcoy",          name: "Chivilcoy",          region: "Interior BA" },
    { geo_code: "san nicolas",        name: "San Nicolás",        region: "Interior BA" },
    { geo_code: "zarate",             name: "Zárate",             region: "Interior BA" },
    { geo_code: "campana",            name: "Campana",            region: "Interior BA" },
    { geo_code: "lujan",              name: "Luján",              region: "Interior BA" },
    { geo_code: "general rodriguez",  name: "General Rodríguez",  region: "Interior BA" },
    { geo_code: "san pedro",          name: "San Pedro",          region: "Interior BA" },
    { geo_code: "ramallo",            name: "Ramallo",            region: "Interior BA" },
    { geo_code: "chacabuco",          name: "Chacabuco",          region: "Interior BA" },
    { geo_code: "lincoln",            name: "Lincoln",            region: "Interior BA" },
    { geo_code: "pehuajo",            name: "Pehuajó",            region: "Interior BA" },
    {
      geo_code: "9 de julio",
      name: "9 de Julio",
      region: "Interior BA",
      aliases: [{ geo_code: "nueve de julio", name: "Nueve de Julio (forma escrita)" }],
    },
    { geo_code: "bragado",            name: "Bragado",            region: "Interior BA" },
    { geo_code: "trenque lauquen",    name: "Trenque Lauquen",    region: "Interior BA" },
    { geo_code: "daireaux",           name: "Daireaux",           region: "Interior BA" },
    { geo_code: "bolivar",            name: "Bolívar",            region: "Interior BA" },
    { geo_code: "coronel suarez",     name: "Coronel Suárez",     region: "Interior BA" },
    { geo_code: "tres arroyos",       name: "Tres Arroyos",       region: "Interior BA" },
    { geo_code: "benito juarez",      name: "Benito Juárez",      region: "Interior BA" },
    { geo_code: "mercedes",           name: "Mercedes",           region: "Interior BA" },
    { geo_code: "balcarce",           name: "Balcarce",           region: "Interior BA" },
    { geo_code: "saladillo",          name: "Saladillo",          region: "Interior BA" },
    {
      geo_code: "25 de mayo",
      name: "25 de Mayo",
      region: "Interior BA",
      aliases: [{ geo_code: "veinticinco de mayo", name: "Veinticinco de Mayo (forma escrita)" }],
    },
    { geo_code: "las flores",         name: "Las Flores",         region: "Interior BA" },
    { geo_code: "rauch",              name: "Rauch",              region: "Interior BA" },
    { geo_code: "carlos casares",     name: "Carlos Casares",     region: "Interior BA" },
    { geo_code: "miramar",            name: "Miramar",            region: "Costa BA" },
    { geo_code: "pinamar",            name: "Pinamar",            region: "Costa BA" },
    { geo_code: "villa gesell",       name: "Villa Gesell",       region: "Costa BA" },
    { geo_code: "mar de ajo",         name: "Mar de Ajó",         region: "Costa BA" },
    { geo_code: "san bernardo",       name: "San Bernardo",       region: "Costa BA" },
    { geo_code: "santa teresita",     name: "Santa Teresita",     region: "Costa BA" },
    { geo_code: "las toninas",        name: "Las Toninas",        region: "Costa BA" },
    { geo_code: "mar del tuyu",       name: "Mar del Tuyú",       region: "Costa BA" },
    { geo_code: "mar de las pampas",  name: "Mar de las Pampas",  region: "Costa BA" },
    { geo_code: "carilo",             name: "Cariló",             region: "Costa BA" },
    { geo_code: "valeria del mar",    name: "Valeria del Mar",    region: "Costa BA" },
    { geo_code: "ostende",            name: "Ostende",            region: "Costa BA" },
];

/** CABA + GBA + ciudades nacionales (sync con Worker detectByCity cityMap). */
export const AR_CITY_CATALOG: CityCatalogEntry[] = (
  [...AR_CABA_GBA_CITY_ROWS, ...AR_NATIONAL_CITY_CATALOG]
).map((c) => ({
  ...c,
  provinceCode: deriveCatalogCityProvinceCode(c.region),
}));

/** Normaliza geo_code (mismo criterio que el Worker sobre cf.city normalizado). */
export function normalizeCityKey(s: string): string {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

/**
 * Claves wishlist a probar para un cf.city (orden: exacto → canonical CABA).
 * Alineado con cloudflare-worker → wishlistCityLookupKeys().
 */
export function wishlistCityLookupKeys(cfCity: string): string[] {
  const cityNorm = normalizeCityKey(cfCity);
  if (!cityNorm) return [];
  const keys = [cityNorm];
  if (cityNorm === "buenos aires") {
    keys.push("caba", "capital federal", "buenos aires city");
  }
  return [...new Set(keys)];
}

export const AR_CITY_CATALOG_KEYS = new Set<string>(
  AR_CITY_CATALOG.flatMap((c) => [c.geo_code, ...(c.aliases ?? []).map((a) => a.geo_code)]),
);

export const AR_CITY_ALIAS_TO_CANONICAL = new Map<string, string>();
for (const c of AR_CITY_CATALOG) {
  AR_CITY_ALIAS_TO_CANONICAL.set(c.geo_code, c.geo_code);
  for (const a of c.aliases ?? []) {
    AR_CITY_ALIAS_TO_CANONICAL.set(a.geo_code, c.geo_code);
  }
}

/**
 * Países gestionables en /admin/wishlist-config (scope=country).
 * DEFAULT = fallback para ISO2 sin fila explícita. AR usa scope=province_ar.
 */
export const INTERNATIONAL_COUNTRIES: { code: string; name: string }[] = [
  { code: "DEFAULT", name: "Todos los demás (default)" },
  ...WORLD_COUNTRIES.filter((c) => c.code !== "AR"),
];

// ─────────────────────────────────────────────────────────────────────────────
// AR — equivalencias de CABA (FUENTE DE VERDAD)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Variantes con las que CABA llega desde el edge, el Worker o el admin.
 *
 * ESTA ES LA FUENTE DE VERDAD. El repo mantiene tres capas separadas a
 * propósito (frontend / api / worker) y `api/` nunca importa de `src/`, así que
 * las otras capas llevan copias espejo:
 *
 *   - api/lib/arProvinceCanonical.ts   → canonicalArProvinceName()
 *   - api/lib/ctaResolver.ts           → AR_PROVINCE_ALIASES.caba
 *   - cloudflare-worker-.../worker.js  → CABA_WISHLIST_EQUIVALENT_KEYS
 *                                      → detectByRegionText() grupo CABA
 *
 * `scripts/validate-ar-geo-coherence.ts` corre en CI y falla si alguna copia se
 * desincroniza de ésta. Si agregás una variante, agregala ACÁ primero y después
 * propagala; el validador te dice exactamente qué capa falta.
 *
 * Por qué existe: Cloudflare emite "Buenos Aires F.D." para la capital y ese
 * literal no estaba en ninguna tabla, así que los overrides de provincia CABA
 * (video y CTA) nunca disparaban para un visitante real.
 *
 * NO incluir el "Buenos Aires" pelado: es ambiguo (es el nombre de la PROVINCIA,
 * cf.regionCode AR-B) y colapsarlo mandaría al conurbano al contenido de la
 * capital. El fingerprint city+region de CABA se resuelve en el Worker, que sí
 * tiene ambas señales.
 */
export const AR_CABA_ALIASES: readonly string[] = [
  "CABA",
  "Buenos Aires F.D.",
  "Buenos Aires F.D",
  "Buenos Aires FD",
  "Ciudad Autónoma de Buenos Aires",
  "Ciudad Autonoma de Buenos Aires",
  "Ciudad de Buenos Aires",
  "Autonomous City of Buenos Aires",
  "Capital Federal",
  "Buenos Aires City",
];

/** Set normalizado (normalizeCityKey) de {@link AR_CABA_ALIASES}. */
export const AR_CABA_ALIAS_KEYS: ReadonlySet<string> = new Set(
  AR_CABA_ALIASES.map(normalizeCityKey),
);

/**
 * cf.city / city_ar keys that mean “capital CABA” (no barrio).
 * Mirror of Worker `CABA_CAPITAL_CITY_AR_KEYS` — capital defers to province_ar CABA
 * so admin “CABA → sin botón” is not overridden by stale city_ar community.
 */
export const AR_CABA_CAPITAL_CITY_AR_KEYS: ReadonlySet<string> = new Set(
  [
    "caba",
    "buenos aires",
    "buenos aires city",
    "capital federal",
    "autonomous city of buenos aires",
    "ciudad autonoma de buenos aires",
    "ciudad de buenos aires",
  ].map(normalizeCityKey),
);

export function isCabaCapitalCityArKey(key: string | null | undefined): boolean {
  if (!key) return false;
  return AR_CABA_CAPITAL_CITY_AR_KEYS.has(normalizeCityKey(key));
}

/** province_ar CABA row matches Worker province string (CABA / Capital Federal / …). */
export function isCabaProvinceGeoCode(geoCode: string | null | undefined): boolean {
  if (!geoCode) return false;
  const k = normalizeCityKey(geoCode);
  if (k === "caba") return true;
  return AR_CABA_ALIAS_KEYS.has(k);
}
